import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import { useDisableCopyAndRightClick } from 'hooks/useDisableCopyAndRightClick';
import EnglishProficiencyForm from './EnglishProficiencyForm';
import { ENGLISH_PROFICIENCY_PRACTICE_QUESTIONS } from './EnglishProficiencyPractice.constants';
import { TEnglishProficiencyAnswers } from './EnglishProficiencyTest.helpers';
import styles from './EnglishProficiencyTest.module.scss';

interface IEnglishProficiencyPractice {
  onFinishPractice: () => void;
}

/**
 * The practice run: the real page, two questions, and nothing behind it.
 *
 * No sitting, so no clock, no saving and no score. What a candidate is
 * rehearsing is the page — that a chosen option is written into the gap, that
 * a typed one is typed into it, that answering marks the card — and every one
 * of those comes from the shared form, unchanged. Anything that made practice
 * a lighter version of the page would be rehearsing the wrong thing.
 *
 * Where the clock sits on a real sitting, this says "Practice", because a page
 * this faithful should not be mistakable for the real one.
 */
const EnglishProficiencyPractice: FC<IEnglishProficiencyPractice> = ({
  onFinishPractice,
}) => {
  const { t } = useTranslation();
  useDisableCopyAndRightClick();

  const [answers, setAnswers] = useState<TEnglishProficiencyAnswers>({});

  const handleChooseAnswer = useCallback(
    (questionIndex: number, selectedAnswerIndex: number) => {
      setAnswers(current => ({
        ...current,
        [questionIndex]: { selectedAnswerIndex, blankAnswers: null },
      }));
    },
    []
  );

  const handleTypeBlanks = useCallback(
    (questionIndex: number, blankAnswers: string[]) => {
      setAnswers(current => ({
        ...current,
        [questionIndex]: { selectedAnswerIndex: null, blankAnswers },
      }));
    },
    []
  );

  // Nothing is sent, so there is nothing to flush when a field loses focus.
  const handleBlurBlanks = useCallback(() => undefined, []);

  return (
    <>
      <HelmetAndPageAnnouncer
        pageTitle={t('englishProficiencyTest.practiceTitle')}
      />
      <EnglishProficiencyForm
        questions={ENGLISH_PROFICIENCY_PRACTICE_QUESTIONS}
        answers={answers}
        onChooseAnswer={handleChooseAnswer}
        onTypeBlanks={handleTypeBlanks}
        onBlurBlanks={handleBlurBlanks}
        disabled={false}
        lead={
          <p className={styles.practiceLead}>
            {t('englishProficiencyTest.practiceLabel')}
          </p>
        }
        submitLabel={t('englishProficiencyTest.finishPractice')}
        onSubmit={onFinishPractice}
      />
    </>
  );
};

export default EnglishProficiencyPractice;
